/**
 * MOCK MODE — a Dreamwell person sees every page full of made-up data.
 *
 * Who: only someone signed in with an `@dreamwell.ai` address (exactly that
 * domain: not a subdomain, so not the local dev key's `…@system.dreamwell.ai`).
 * Everyone else never sees the switch, and a preference left in their browser
 * does nothing.
 *
 * What: while it is on, `src/mocks` answers every API call in the browser from
 * one invented brand (its team, campaigns, creators, deals, chats…) and nothing
 * reaches the API — reads or writes. Writes land in that invented world and
 * last until the page reloads. Signing in and the account's own settings
 * (profile, sessions) stay real.
 *
 * Unlike web-v2's demo mode (a flag on a real team that dresses its real
 * creators in a synthesized pipeline), this belongs to the person, not a team:
 * it can never make a customer's workspace show invented data, because it never
 * shows a real workspace at all.
 *
 * The choice is kept per browser and changed only from the account menu's
 * switch (no URL turns it on). Changing it reloads the page, so no real and
 * invented data ever share a cache.
 */
const STORAGE_KEY = 'dreamwell:mock-mode';

export function canUseMockMode(email: string | null | undefined): boolean {
  return typeof email === 'string' && /@dreamwell\.ai$/i.test(email.trim());
}

/** The browser's choice. */
export function mockModePreferred(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Turn it on or off; the page reloads. */
export function setMockMode(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
  window.location.reload();
}
